import { Component, EventEmitter, Input, Output } from '@angular/core';
import { GraphAnnotationModel, GraphAnnotationType, PatientBehaviorModel, PatientProgramModel, PatientTargetModel, SkillAreaModel } from '../../../core/api/models';
import { GraphApiService } from '../../../core/api/services';
import { Utils } from '../../../core/utils';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
  selector: 'app-annotation',
  templateUrl: './annotation.component.html',
  styleUrl: './annotation.component.css',
  standalone: false,
})
export class AnnotationComponent {
  @Input() targets: PatientTargetModel[] = [];
  @Input() behaviors: PatientBehaviorModel[] = [];
  @Input() programs: PatientProgramModel[] = [];
  @Input() skillAreas: SkillAreaModel[] = [];
  @Input() annotation: GraphAnnotationModel | null = null;
  @Input() canManageAnnotations: boolean = false;
  @Output() onEditAnnotationClicked = new EventEmitter<GraphAnnotationModel>();
  @Output() onDeleteAnnotation = new EventEmitter<void>();

  constructor(private graphApiService: GraphApiService, private alertService: AlertService) {
  }

  _isExpanded:boolean = false;
  @Input()
  get isExpanded(): boolean {
    return !!this._isExpanded;
  }

  set isExpanded(val: boolean) {
    this._isExpanded = !!val;
    this.isExpandedChange.emit(this._isExpanded); // Emit changes
  }

  @Output() isExpandedChange = new EventEmitter<boolean>();
  
  toggleCollapsed() {
    this.isExpanded = !this.isExpanded;
  }

  graphAnnotationTypeLabel(annotationType: number | undefined) {
    if (!annotationType) return 'Unspecified';
    return Utils.graphAnnotationTypeLabel(annotationType);
  }

  editAnnotation() {
    console.log(this.annotation);
    if (this.annotation) {
      this.onEditAnnotationClicked.emit(this.annotation);
    }
  }

  deleteAnnotation() {
    this.alertService.confirm('Are you sure you want to delete this annotation?', 'Delete Annotation').subscribe((result) => {
      if (result) {
        this.graphApiService.MarkGraphAnnotationAsDeleted({
          graphAnnotationID: this.annotation?.ID ?? 0,
        }).subscribe((response) => {
          this.alertService.success('Annotation deleted successfully');
          this.onDeleteAnnotation.emit();
        }, (error) => {
          this.alertService.error('Error deleting annotation - ' + Utils.getErrorMessageFromErrorResponse(error));
        });
      }
    });
  }

  associationsForDisplay() {
    if (this.annotation) {
      const targetNames = this.targets.filter(t => this.annotation?.PatientTargetIDs?.includes(t.ID ?? 0)).map(t => t.TargetName + '(T)');
      const behaviorNames = this.behaviors.filter(b => this.annotation?.PatientBehaviorIDs?.includes(b.ID ?? 0)).map(b => b.PatientBehaviorName + '(B)');
      const programNames = this.programs.filter(p => this.annotation?.PatientProgramIDs?.includes(p.ID ?? 0)).map(p => p.ProgramName + '(P)');
      const skillAreaNames = this.skillAreas.filter(s => this.annotation?.PatientSkillAreaIDs?.includes(s.ID ?? 0)).map(s => s.SkillAreaName + '(SA)');

      return [...targetNames, ...behaviorNames, ...programNames, ...skillAreaNames].join(', ');
    }
    return '';
  }

  shouldDisplayStartDate() {
    return this.annotation?.GraphAnnotationType === GraphAnnotationType.XAxisRange || this.annotation?.GraphAnnotationType === GraphAnnotationType.XAxis;
  }
 
  shouldDisplayEndDate() {
    return this.annotation?.GraphAnnotationType === GraphAnnotationType.XAxisRange;
  }

  shouldDisplayYAxisMin() {
    return this.annotation?.GraphAnnotationType === GraphAnnotationType.YAxisRange || this.annotation?.GraphAnnotationType === GraphAnnotationType.YAxis;
  }

  shouldDisplayYAxisMax() {
    return this.annotation?.GraphAnnotationType === GraphAnnotationType.YAxisRange;
  }

  shouldDisplayBreakSeries() {
    return this.annotation?.GraphAnnotationType === GraphAnnotationType.XAxis;
  }
}
